import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Row, Col, Button, message, Collapse, Tooltip, Badge } from "antd";
import { CalendarOutlined, LeftOutlined, RightOutlined, HomeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { useTranslation } from "react-i18next";
import styles from './ThreeMonthCalendar.module.css';

import 'dayjs/locale/de';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale("de");

const { Panel } = Collapse;

const ThreeMonthWeekPicker = ({ onWeekSelect, isMobile, selectedWeek, selectedYear }) => {
  const [startMonth, setStartMonth] = useState(dayjs().startOf("month"));
  const [internalSelectedWeek, setInternalSelectedWeek] = useState(dayjs()); // Initialize with current week
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredDate, setHoveredDate] = useState(null);
  const [animating, setAnimating] = useState(false);
  const { t } = useTranslation();

  // Update internal state when parent props change
  useEffect(() => {
    if (selectedWeek && selectedYear) {
      // Create a dayjs object for the given year and week
      const newDate = dayjs().year(selectedYear).isoWeek(selectedWeek).startOf('isoWeek');
      setInternalSelectedWeek(newDate);
      // Update start month to show the selected week's month
      setStartMonth(newDate.startOf("month"));
    }
  }, [selectedWeek, selectedYear]);

  // Initialize with current week on component mount - only run once
  useEffect(() => {
    if (!selectedWeek || !selectedYear) {
      const currentWeek = dayjs();
      setInternalSelectedWeek(currentWeek);
      const year = currentWeek.year();
      const week = currentWeek.isoWeek();
      onWeekSelect({ year, week });
    }
  }, []); // Empty dependency array to run only once on mount

  const handleWeekSelect = useCallback((date) => {
    console.log('Calendar: Week selected', { date: date.format('YYYY-MM-DD'), week: date.isoWeek(), year: date.year() });
    setInternalSelectedWeek(date);
    const year = date.year();
    const week = date.isoWeek();
    onWeekSelect({ year, week });
    
    // Enhanced user feedback with better messaging
    message.success({
      content: `${t('selectedWeek')}: ${t('year')} ${year}, ${t('calenderWeek')} ${week}`,
      duration: 2,
      style: {
        marginTop: '10vh',
      },
    });
  }, [onWeekSelect, t]);

  const isInSelectedWeek = useCallback((date) => {
    if (!internalSelectedWeek) return false;
    const start = internalSelectedWeek.startOf("isoWeek");
    const end = internalSelectedWeek.endOf("isoWeek");
    const isInWeek = date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
    return isInWeek;
  }, [internalSelectedWeek]);

  const isInHoveredWeek = useCallback((date) => {
    if (!hoveredDate) return false;
    const start = hoveredDate.startOf("isoWeek");
    const end = hoveredDate.endOf("isoWeek");
    return date.isSameOrAfter(start, 'day') && date.isSameOrBefore(end, 'day');
  }, [hoveredDate]);

  const handleMonthNavigation = useCallback((direction) => {
    setAnimating(true);
    setTimeout(() => {
      if (direction === 'prev') {
        setStartMonth(prev => prev.subtract(1, "month"));
      } else if (direction === 'next') {
        setStartMonth(prev => prev.add(1, "month"));
      } else {
        setStartMonth(dayjs().startOf("month"));
      }
      setAnimating(false);
    }, 150);
  }, []);

  const handleDateHover = useCallback((date, isHovering) => {
    if (!isMobile) {
      setHoveredDate(isHovering ? date : null);
    }
  }, [isMobile]);

  const renderCalendars = () => {
    const monthsToRender = isMobile ? [0] : [0, 1, 2];

    return monthsToRender.map((offset) => {
      const month = startMonth.add(offset, "month");
      const isCurrentMonth = month.isSame(dayjs(), 'month');

      return (
        <Col span={isMobile ? 24 : 8} key={offset} className={styles.calendarColumn}>
          <div className={`${styles.calendarWrapper} ${animating ? styles.animating : ''}`}>
            <Calendar
              fullscreen={false}
              value={month}
              onSelect={handleWeekSelect}
              className={styles.modernCalendar}
              mode="month"
              dateFullCellRender={(date) => {
                const isToday = date.isSame(dayjs(), "day");
                const isSelected = isInSelectedWeek(date);
                const isHovered = isInHoveredWeek(date);
                const isWeekend = date.day() === 0 || date.day() === 6;
                const isCurrentMonthDate = date.isSame(month, 'month');
                const weekNumber = date.isoWeek();
                
                let cellClass = styles.dateCell;
                if (isToday) cellClass += ` ${styles.today}`;
                if (isSelected) cellClass += ` ${styles.selected}`;
                if (isHovered && !isSelected) cellClass += ` ${styles.hovered}`;
                if (isWeekend) cellClass += ` ${styles.weekend}`;
                if (!isCurrentMonthDate) cellClass += ` ${styles.otherMonth}`;

                return (
                  <div 
                    className={cellClass}
                    onMouseEnter={() => handleDateHover(date, true)}
                    onMouseLeave={() => handleDateHover(date, false)}
                    onClick={() => handleWeekSelect(date)}
                    style={{ cursor: 'pointer' }}
                  >
                    <span className={styles.dateNumber}>{date.date()}</span>
                    {isToday && <Badge className={styles.todayBadge} />}
                    {date.date() === 1 && (
                      <Tooltip title={`${t('calenderWeek')} ${weekNumber}`}>
                        <span className={styles.weekNumber}>W{weekNumber}</span>
                      </Tooltip>
                    )}
                  </div>
                );
              }}
              
              headerRender={() => (
                <div className={styles.calendarHeader}>
                  <div className={`${styles.monthTitle} ${isCurrentMonth ? styles.currentMonth : ''}`}>
                    {month.format("MMMM YYYY")}
                    {isCurrentMonth && <Badge className={styles.currentMonthBadge} />}
                  </div>
                </div>
              )}
            />
          </div>
        </Col>
      );
    });
  };

  const getCurrentWeekInfo = () => {
    if (!internalSelectedWeek) return null;
    const weekStart = internalSelectedWeek.startOf('isoWeek');
    const weekEnd = internalSelectedWeek.endOf('isoWeek');
    return {
      weekNumber: internalSelectedWeek.isoWeek(),
      year: internalSelectedWeek.year(),
      dateRange: `${weekStart.format('DD.MM')} - ${weekEnd.format('DD.MM.YYYY')}`
    };
  };

  const weekInfo = getCurrentWeekInfo();

  return (
    <div className={styles.calendarContainer}>
      <Collapse 
        defaultActiveKey={[]} 
        ghost
        className={styles.modernCollapse}
        onChange={(keys) => setIsExpanded(keys.length > 0)}
      >
        <Panel 
          header={
            <div className={styles.panelHeader}>
              <div className={styles.headerLeft}>
                <CalendarOutlined className={styles.calendarIcon} />
                <span className={styles.headerTitle}>{t("showCalendar")}</span>
              </div>
              {weekInfo && (
                <div className={styles.headerRight}>
                  <Badge 
                    count={weekInfo.weekNumber} 
                    className={styles.weekBadge}
                    title={`${t('calenderWeek')} ${weekInfo.weekNumber}`}
                  />
                  <span className={styles.dateRange}>{weekInfo.dateRange}</span>
                </div>
              )}
            </div>
          } 
          key="1"
          className={styles.calendarPanel}
        >
          {/* Enhanced Navigation Controls */}
          <div className={styles.navigationControls}>
            <div className={styles.navButtonGroup}>
              <Tooltip title={t("previousMonth")}>
                <Button 
                  type="text" 
                  icon={<LeftOutlined />} 
                  onClick={() => handleMonthNavigation('prev')}
                  className={styles.navButton}
                  loading={animating}
                />
              </Tooltip>
              
              <Tooltip title={t("currentMonth")}>
                <Button 
                  type="primary" 
                  icon={<HomeOutlined />} 
                  onClick={() => handleMonthNavigation('today')}
                  className={styles.todayButton}
                  ghost
                >
                  {!isMobile && t("today")}
                </Button>
              </Tooltip>
              
              <Tooltip title={t("nextMonth")}>
                <Button 
                  type="text" 
                  icon={<RightOutlined />} 
                  onClick={() => handleMonthNavigation('next')}
                  className={styles.navButton}
                  loading={animating}
                />
              </Tooltip>
            </div>
            
            {weekInfo && (
              <div className={styles.weekInfo}>
                <span className={styles.weekLabel}>{t('selectedWeek')}:</span>
                <Badge 
                  count={`W${weekInfo.weekNumber}`} 
                  className={styles.selectedWeekBadge}
                />
                <span className={styles.yearLabel}>{weekInfo.year}</span>
              </div>
            )}
          </div>

          {/* Enhanced Calendar Grid */}
          <div className={styles.calendarsContainer}>
            <Row gutter={[16, 16]} className={styles.calendarRow}>
              {renderCalendars()}
            </Row>
          </div>
          
          {/* Week Selection Helper */}
          {hoveredDate && !isMobile && (
            <div className={styles.weekPreview}>
              <span className={styles.previewLabel}>{t('weekPreview')}:</span>
              <span className={styles.previewWeek}>
                {t('calenderWeek')} {hoveredDate.isoWeek()} - 
                {hoveredDate.startOf('isoWeek').format('DD.MM')} bis {hoveredDate.endOf('isoWeek').format('DD.MM.YYYY')}
              </span>
            </div>
          )}
        </Panel>
      </Collapse>
    </div>
  );
};

export default ThreeMonthWeekPicker;
